import { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';

import { Donut } from '../components/charts/Donut';
import { EquityCurve } from '../components/charts/EquityCurve';
import { Sparkline } from '../components/charts/Sparkline';
import { Chip } from '../components/primitives/Chip';
import { Container } from '../components/primitives/Container';
import { Pulse } from '../components/primitives/Pulse';
import { SectionHead } from '../components/primitives/SectionHead';
import { StatBlock } from '../components/primitives/StatBlock';
import { useContent } from '../content/load';
import { equityCurve, mulberry32 } from '../lib/equity-curve';
import { formatMoney } from '../lib/format';
import type { Strategy } from '../content/schemas';

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: ${(p) => p.theme.step[6]}px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.section`
  border: 1px solid ${(p) => p.theme.color.border.soft};
  border-radius: 12px;
  background: ${(p) => p.theme.color.bg[1]};
  padding: ${(p) => p.theme.step[5]}px;
`;

const PanelHead = styled.h3`
  margin: 0 0 12px;
  font-family: ${(p) => p.theme.font.serif};
  font-size: ${(p) => p.theme.step[5]}px;
  letter-spacing: -0.01em;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${(p) => p.theme.step[5]}px;
  padding-bottom: ${(p) => p.theme.step[5]}px;
`;

const StratList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StratRow = styled.li`
  display: grid;
  grid-template-columns: 1fr 80px;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid ${(p) => p.theme.color.border.faint};

  &:last-child { border-bottom: none; }
`;

interface DashboardSnapshot {
  pnlCurve: number[];
  pnlToday: number;
  exposure: number;
  liveCount: number;
  positions: { name: string; px: string; qty: number; pnl: number }[];
  orders: { id: string; sym: string; side: 'BUY' | 'SELL'; qty: number; px: string }[];
  allocation: { label: string; value: number; color: string }[];
}

function generateDashboardMockData(
  strategies: Strategy[],
  cat: { amber: string; cyan: string; violet: string; rose: string; lime: string },
): DashboardSnapshot {
  const seed = strategies.reduce((s, x) => s + x.trades, 1);
  const r = mulberry32(seed);
  const pnlCurve = equityCurve({ seed, steps: 240, drift: 0.0009, vol: 0.014 });
  const last = pnlCurve[pnlCurve.length - 1] ?? 100;
  const prev = pnlCurve[pnlCurve.length - 2] ?? 100;
  const pnlToday = ((last - prev) / prev) * 100000;
  const exposure = 0.6 + r() * 0.3;
  const liveCount = strategies.filter((s) => s.status === 'live').length;

  const positions = strategies.slice(0, 5).map((s) => ({
    name: s.name,
    px: (1000 + r() * 4000).toFixed(2),
    qty: Math.round(r() * 50 + 5),
    pnl: (r() - 0.4) * 4200,
  }));
  const orders = strategies.slice(0, 5).map((s, i) => ({
    id: `O-${10000 + i}`,
    sym: s.code.split('-')[1] ?? s.id.toUpperCase(),
    side: r() > 0.5 ? ('BUY' as const) : ('SELL' as const),
    qty: Math.round(r() * 30 + 1),
    px: (50 + r() * 400).toFixed(2),
  }));
  const colors = [cat.amber, cat.cyan, cat.violet, cat.rose, cat.lime];
  const allocation = strategies.slice(0, 5).map((s, i) => ({
    label: s.name,
    value: Math.round(r() * 30 + 5),
    color: colors[i % colors.length] ?? cat.amber,
  }));
  return { pnlCurve, pnlToday, exposure, liveCount, positions, orders, allocation };
}

export default function Dashboard() {
  const { strategies } = useContent();
  const theme = useTheme();
  const data = useMemo(
    () => generateDashboardMockData(strategies, theme.color.category),
    [strategies, theme.color.category],
  );

  return (
    <Container>
      <SectionHead
        eyebrow="DASHBOARD"
        heading="Live (mock) book."
        body="Demo data for the v1 build. Real execution data lands when the backend does."
      />

      <Stats>
        <StatBlock label="P&L (today)" value={formatMoney(data.pnlToday)} />
        <StatBlock
          label="EXPOSURE"
          value={`${(data.exposure * 100).toFixed(1)}%`}
          detail="of buying power"
        />
        <StatBlock label="LIVE STRATS" value={String(data.liveCount)} />
        <StatBlock label="WIN RATE" value="62.1%" detail="trailing 30d" />
      </Stats>

      <Grid>
        <Panel>
          <PanelHead>P&L (240d)</PanelHead>
          <EquityCurve data={data.pnlCurve} height={260} />
        </Panel>
        <Panel>
          <PanelHead>Allocation</PanelHead>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, justifyContent: 'center' }}>
            <Donut data={data.allocation} size={180} />
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 12 }}>
              {data.allocation.map((a) => (
                <li key={a.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: a.color,
                    }}
                  />
                  {a.label}
                </li>
              ))}
            </ul>
          </div>
        </Panel>

        <Panel>
          <PanelHead>Active strategies</PanelHead>
          <StratList>
            {strategies
              .filter((s) => s.status === 'live')
              .slice(0, 6)
              .map((s) => (
                <StratRow key={s.id}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Pulse />
                      <strong>{s.name}</strong>
                      <Chip $variant="amber">{s.tier}</Chip>
                    </div>
                    <small style={{ color: 'var(--text-3)' }}>
                      {s.monthly} monthly · {s.lifetime} lifetime
                    </small>
                  </div>
                  <span style={{ color: 'var(--amber)' }}>
                    <Sparkline
                      data={equityCurve({ seed: s.trades, steps: 32 })}
                      width={80}
                      height={24}
                    />
                  </span>
                </StratRow>
              ))}
          </StratList>
        </Panel>
        <Panel>
          <PanelHead>Recent orders</PanelHead>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: 'var(--text-3)', textAlign: 'left' }}>
                <th>ID</th>
                <th>SYM</th>
                <th>Side</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th style={{ textAlign: 'right' }}>Px</th>
              </tr>
            </thead>
            <tbody>
              {data.orders.map((o) => (
                <tr key={o.id} style={{ borderTop: '1px solid var(--border-faint)' }}>
                  <td style={{ fontFamily: 'var(--font-mono)' }}>{o.id}</td>
                  <td>{o.sym}</td>
                  <td style={{ color: o.side === 'BUY' ? 'var(--up)' : 'var(--down)' }}>
                    {o.side}
                  </td>
                  <td style={{ textAlign: 'right' }}>{o.qty}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{o.px}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </Grid>
    </Container>
  );
}
